
import React from 'react';
import { useNavigate } from 'react-router-dom';

interface Destination {
  title: string;
  duration: string;
  description: string[];
  image: string;
  buttonText: string;
  buttonAction: string;
}

const destinations: Destination[] = [
  {
    title: "Lord Howe Island, New South Wales",
    duration: "Lord Howe Island and Around, NSW 14 Night / 15 Days",
    description: [
      "A wildlife sanctuary known as “Australia’s Galápagos,” where kangaroos, koalas, sea lions, and echidnas roam freely.",
      "Explore Flinders Chase National Park, Remarkable Rocks, and secluded beaches."
    ],
    image: "/images/australia-dest-1.jpg",
    buttonText: "Explore More",
    buttonAction: "explore"
  },
  {
    title: "Kangaroo Island, South Australia",
    duration: "Around Kangaroo Island - Tour of 09 Night / 10 Days",
    description: [
      "A wildlife sanctuary known as “Australia’s Galápagos,” where kangaroos, koalas, sea lions, and echidnas roam freely.",
      "Explore Flinders Chase National Park, Remarkable Rocks, and secluded beaches."
    ],
    image: "/images/australia-dest-2.jpg",
    buttonText: "Enquire Now",
    buttonAction: "enquire"
  },
  {
    title: "Freycinet Peninsula, Tasmania",
    duration: "Amazing Tasmania 10 Night / 11 Days",
    description: [
      "Famous for Wineglass Bay, one of the world's most photogenic beaches.",
      "The area offers gourmet seafood, pink granite mountains, and eco-luxury lodges surrounded by wilderness."
    ],
    image: "/images/australia-dest-3.jpg",
    buttonText: "Enquire Now",
    buttonAction: "enquire"
  },
  {
    title: "Ningaloo Reef, Western Australia",
    duration: "Tour Around Ningaloo Reef - Tour of 6 Night / 7 Days",
    description: [
      "Less crowded than the Great Barrier Reef, this UNESCO site lets you swim alongside whale sharks (March–August) and manta rays. Pristine beaches and snorkeling right off the shore add to the charm."
    ],
    image: "/images/australia-dest-4.jpg",
    buttonText: "Enquire Now",
    buttonAction: "enquire"
  },
  {
    title: "The Kimberley, Western Australia",
    duration: "Around Kimberley 09 Night / 10 Days",
    description: [
      "​A remote wilderness of red gorges, waterfalls, and Aboriginal rock art.",
      "Highlights include the Horizontal Falls, Bungle Bungles, and luxury cruises along the rugged coastline."
    ],
    image: "/images/australia-dest-5.jpg",
    buttonText: "Enquire Now",
    buttonAction: "enquire"
  },
  {
    title: "Norfolk Island, South Pacific Territory",
    duration: "Explore Around Norfolk Island - Tour of 10 Night / 11 Days",
    description: [
      "A remote island with convict history, Norfolk pines, and a blend of Polynesian and colonial culture.",
      "Perfect for history buffs, hikers, and those seeking a slow-paced escape."
    ],
    image: "/images/australia-dest-6.jpg",
    buttonText: "Enquire Now",
    buttonAction: "enquire"
  },
  {
    title: "Grampians National Park, Victoria",
    duration: "All of Victoria with Grampians National Park 09 Night / 10 Days",
    description: [
      "A hidden gem for hikers and culture lovers. Known for dramatic sandstone mountains.",
      "Aboriginal rock art, wildflowers, and boutique wineries. Sunrise at Boroka Lookout is unforgettable."
    ],
    image: "/images/australia-dest-7.jpg",
    buttonText: "Enquire Now",
    buttonAction: "enquire"
  },
  {
    title: "The Daintree Rainforest & Cape Tribulation, Queensland",
    duration: "Queensland and around - Tour of 09 Night / 10 Days",
    description: [
      "The world's oldest rainforest where the reef meets the jungle.",
      "Visitors can zipline through the canopy, spot cassowaries, and explore mangrove rivers teeming with crocodiles."
    ],
    image: "/images/australia-dest-8.jpg",
    buttonText: "Enquire Now",
    buttonAction: "enquire"
  },
  {
    title: "Flinders Ranges, South Australia",
    duration: "All of South Australia 14 Night / 15 Days",
    description: [
      "A rugged outback region showcasing ancient landscapes, Aboriginal heritage, and stargazing in one of the clearest skies in the world.",
      "The amphitheater-like Wilpena Pound is the highlight."
    ],
    image: "/images/australia-dest-9.jpg",
    buttonText: "Enquire Now",
    buttonAction: "enquire"
  },
  {
    title: "Rottnest Island, Western Australia",
    duration: "Rottnest Island and Around  - Tour of 07 Night / 08 Days",
    description: [
      "A car-free island famous for its friendly quokkas (the world's happiest animals).",
      "With over 60 beaches and bays, it's perfect for cycling, snorkeling, and relaxed island vibes."
    ],
    image: "/images/australia-dest-10.jpg",
    buttonText: "Enquire Now",
    buttonAction: "enquire"
  }
];

export const DestinationsSection: React.FC = () => {
  const navigate = useNavigate();

  const handleButtonClick = (action: string, title: string) => {
    if (action === 'enquire') {
      navigate(`/contact?subject=${encodeURIComponent('Enquiry about ' + title)}`);
      return;
    }

    if (action === 'explore') {
      navigate('/group-tours');
      return;
    }
  };

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">

          </h2>
        </div>

        <div className="space-y-16">
          {destinations.map((destination, index) => (
            <div
              key={index}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-8 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
                }`}
            >
              <div className={`space-y-6 ${index % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                <h3 className="text-2xl lg:text-3xl font-bold text-gray-900">
                  {destination.title}
                </h3>
                <p className="text-lg text-emerald-600 font-medium">
                  {destination.duration}
                </p>
                <ul className="space-y-3">
                  {destination.description.map((desc, descIndex) => (
                    <li key={descIndex} className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0"></div>
                      <p className="text-gray-700 leading-relaxed">{desc}</p>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => handleButtonClick(destination.buttonAction, destination.title)}
                  className="inline-flex items-center px-6 py-3 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  {destination.buttonText}
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center ml-2"></i>
                </button>
              </div>

              <div className={`${index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}`}>
                <img
                  src={destination.image}
                  alt={destination.title}
                  className="w-full h-80 object-cover rounded-lg shadow-lg"
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
